import type { Alternative } from '../mdf/fieldDefinition';
import type { AlternativesType } from './mdfModels';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import { isArrayOfString } from '../util/general';

export interface OptionList {
  id: string;
  name: string;
  optionListType: OptionListTypes;
  alternatives?: AlternativesType;
  modifiedOn: string;
}

export interface OptionListIdsDto {
  ids: string[];
}

export interface OptionListDto {
  id: string;
  name: string;
  optionListType: OptionListTypes,
  alternatives: AlternativesType;
}

export type OptionListTypes = 'choice' | 'treeChoice';

export type UpdateOptionListDto = Omit<OptionListDto, 'id'> & { optionListType: OptionListTypes | undefined };

export type CreateOptionListDto = UpdateOptionListDto & { id?: string, optionListType: OptionListTypes | undefined };

export const VALID_OPTION_LIST_ID_REGEXP = /^[a-zA-Z0-9_]+$/;

export function isUpdateOptionListDto(obj: unknown): obj is UpdateOptionListDto {
  if (!isObject(obj)) {
    return false;
  }
  const probably = obj as Partial<UpdateOptionListDto>;
  const optionListType = probably.optionListType;

  if (optionListType && !isValidOptionListType(optionListType)) {
    return false;
  }
  if (optionListType === 'treeChoice' && isTreeChoiceAlternatives(probably.alternatives)) {
    return true;
  }
  // For backward compatibility the update request have optionListType optional.
  // If optionListType is missing we consider it as choice.
  return areNameAndAlternativesValid(probably);
}

export function isCreateOptionListDto(obj: unknown): obj is CreateOptionListDto {
  if (!isObject(obj)) {
    return false;
  }
  const probably = obj as Partial<CreateOptionListDto>;
  const optionListType = probably.optionListType;

  if (optionListType && !isValidOptionListType(optionListType)) {
    return false;
  }
  if (optionListType === 'treeChoice' && !isTreeChoiceAlternatives(probably.alternatives)) {
    return false;
  }
  // For backward compatibility the create request have optionListType optional.
  // If optionListType is missing we consider it as choice.
  if ((!optionListType || optionListType === 'choice') && !areNameAndAlternativesValid(probably)) {
    return false;
  }
  if (obj.id) {
    return typeof obj.id === 'string' && VALID_OPTION_LIST_ID_REGEXP.test(obj.id);
  }
  return true;
}

function areNameAndAlternativesValid(obj: Partial<UpdateOptionListDto>): boolean {
  if (typeof obj.name !== 'string') {
    return false;
  }
  if (obj.optionListType && obj.optionListType !== 'choice') {
    return false;
  }

  if (!Array.isArray(obj.alternatives)) {
    return false;
  }
  for (const a of obj.alternatives as Alternative[]) {
    if (typeof a.label !== 'string') {
      return false;
    }
    if (typeof a.value !== 'string' && typeof a.value !== 'number' && typeof a.value !== 'boolean') {
      return false;
    }
  }
  return true;
}

export function getChoiceAlternatives(data: { optionListType: OptionListTypes, alternatives: AlternativesType | undefined}) : Alternative[] | undefined {
  if (areNameAndAlternativesValid(data)) {
    return data.alternatives as Alternative[];
  }
  return undefined;
}

export function getTreeChoiceAlternatives(data: { optionListType: OptionListTypes, alternatives: AlternativesType | undefined}) : string[][] | undefined {
  if (isTreeChoiceAlternatives(data.alternatives)) {
    return data.alternatives as string[][];
  }
  return undefined;
}

export function isTreeChoiceAlternatives(data: unknown): data is string[][] {
  return Array.isArray(data) && data.every(isArrayOfString);
}

function isValidOptionListType(type: unknown): type is OptionListTypes {
  return (type === 'choice' || type === 'treeChoice');
}
